import { prepareProfileMetaContextHeaders } from '@entities/users/apis/meta-profile-context'
import { podoFetcher } from '@server/infrastructure/external-apis/podo/client'
import { z } from 'zod'

import {
  krAcquisitionSchema,
  krPaymentOrderSchema,
  krPaymentRecoverySchema,
  krOrderResumePath,
  isKrOrderEditable,
  isKrOrderRetired,
  type PrepareKrPaymentOrderRequest,
} from './model'

const ROOT = '/api/v2/payment/purchases'
export async function hasKrPurchaseCapability(bearerToken?: string) {
  try {
    const response = await podoFetcher<unknown>(
      {
        url: `${ROOT}/capabilities`,
        method: 'GET',
        header: bearerToken ? { Authorization: `Bearer ${bearerToken}` } : {},
      },
      { throwOnErrorResult: true },
    )
    z.object({ checkoutProtocol: z.literal('KR_PRODUCT_V1') }).parse(response.data)
    return true
  } catch (error) {
    const status = typeof error === 'object' && error !== null && 'status' in error ? error.status : null
    // Older backends authenticate unknown URLs before routing anonymous requests to a 404.
    if (status === 404 || status === 405 || (!bearerToken && status === 401)) {
      return false
    }
    throw error
  }
}

export async function getKrAcquisition(subscribeId: string, bearerToken: string) {
  // Only this dedicated capability probe may downgrade an old backend. Catalog errors never do.
  if (!(await hasKrPurchaseCapability(bearerToken))) {
    return krAcquisitionSchema.parse({
      mode: 'LEGACY',
      reason: 'NOT_PROVISIONED',
      subscribeId,
      product: null,
      checkoutProtocol: null,
    })
  }
  const response = await podoFetcher<unknown>(
    {
      url: `${ROOT}/acquisition?${new URLSearchParams({ subscribeId })}`,
      method: 'GET',
      header: { Authorization: `Bearer ${bearerToken}` },
    },
    { throwOnErrorResult: true },
  )
  const acquisition = krAcquisitionSchema.parse(response.data)
  if (
    acquisition.subscribeId !== subscribeId ||
    (acquisition.mode === 'PRODUCT' &&
      !acquisition.product.details.some(
        (detail) => detail.targetType === 'SUBSCRIBE' && detail.targetId === subscribeId,
      ))
  ) {
    throw new Error('KR_ACQUISITION_MISMATCH')
  }
  return acquisition
}
export async function prepareKrPaymentOrder(
  request: PrepareKrPaymentOrderRequest,
  bearerToken: string,
  options?: { captureTrialContext?: boolean },
) {
  const response = await podoFetcher<unknown>(
    {
      url: `${ROOT}/orders`,
      method: 'POST',
      header: {
        Authorization: `Bearer ${bearerToken}`,
        ...(options?.captureTrialContext === true ? await prepareProfileMetaContextHeaders(bearerToken) : {}),
      },
      body: { ...request },
    },
    { throwOnErrorResult: true },
  )
  return krPaymentOrderSchema.parse(response.data)
}
export async function getKrPaymentOrder(orderId: string, bearerToken: string) {
  const response = await podoFetcher<unknown>(
    {
      url: `${ROOT}/orders/${encodeURIComponent(orderId)}`,
      method: 'GET',
      header: { Authorization: `Bearer ${bearerToken}` },
    },
    { throwOnErrorResult: true },
  )
  const order = krPaymentOrderSchema.parse(response.data)
  if (order.orderId !== orderId) {
    throw new Error('KR_PAYMENT_ORDER_MISMATCH')
  }
  return order
}
export async function attemptKrPaymentOrder(orderId: string, bearerToken: string) {
  const response = await podoFetcher<unknown>(
    {
      url: `${ROOT}/orders/${encodeURIComponent(orderId)}/attempts`,
      method: 'POST',
      header: { Authorization: `Bearer ${bearerToken}` },
    },
    { throwOnErrorResult: true },
  )
  const order = krPaymentOrderSchema.parse(response.data)
  if (order.orderId !== orderId || typeof order.canExecute !== 'boolean') {
    throw new Error('KR_PAYMENT_ORDER_MISMATCH')
  }
  return order
}

export async function getKrAcquisitions(subscribeIds: string[], bearerToken?: string) {
  if (!subscribeIds.length || !(await hasKrPurchaseCapability(bearerToken))) {
    return []
  }
  const query = new URLSearchParams({ subscribeIds: [...new Set(subscribeIds)].join(',') })
  const response = await podoFetcher<unknown>(
    {
      url: bearerToken ? `${ROOT}/acquisitions?${query}` : `/api/v1/products/kr-acquisition?${query}`,
      method: 'GET',
      header: bearerToken ? { Authorization: `Bearer ${bearerToken}` } : {},
    },
    { throwOnErrorResult: true },
  )
  const acquisitions = z.array(krAcquisitionSchema).parse(response.data)
  if (
    acquisitions.length !== new Set(subscribeIds).size ||
    new Set(acquisitions.map((item) => item.subscribeId)).size !== acquisitions.length ||
    acquisitions.some(
      (item) =>
        !subscribeIds.includes(item.subscribeId) ||
        (item.mode === 'PRODUCT' &&
          !item.product.details.some(
            (detail) => detail.targetType === 'SUBSCRIBE' && detail.targetId === item.subscribeId,
          )),
    )
  ) {
    throw new Error('KR_ACQUISITION_MISMATCH')
  }
  return acquisitions
}

export async function cancelKrPaymentOrder(orderId: string, bearerToken: string, options?: { onlyPrepared?: boolean }) {
  const response = await podoFetcher<unknown>(
    {
      url: `${ROOT}/orders/${encodeURIComponent(orderId)}/cancellations${options?.onlyPrepared ? '?onlyPrepared=true' : ''}`,
      method: 'POST',
      header: { Authorization: `Bearer ${bearerToken}` },
    },
    { throwOnErrorResult: true },
  )
  const order = krPaymentOrderSchema.parse(response.data)
  if (order.orderId !== orderId) {
    throw new Error('KR_PAYMENT_ORDER_MISMATCH')
  }
  return order
}

export async function getActiveKrPaymentOrder(bearerToken: string) {
  const response = await podoFetcher<unknown>(
    { url: `${ROOT}/orders/active`, method: 'GET', header: { Authorization: `Bearer ${bearerToken}` } },
    { throwOnErrorResult: true },
  )
  return z.object({ order: krPaymentOrderSchema.nullable() }).parse(response.data).order
}

export async function recoverKrPaymentOrder(orderId: string, bearerToken: string) {
  const response = await podoFetcher<unknown>(
    {
      url: `${ROOT}/orders/${encodeURIComponent(orderId)}/recover`,
      method: 'POST',
      header: { Authorization: `Bearer ${bearerToken}` },
    },
    { throwOnErrorResult: true },
  )
  const recovery = krPaymentRecoverySchema.parse(response.data)
  if (recovery.order.orderId !== orderId) {
    throw new Error('KR_PAYMENT_ORDER_MISMATCH')
  }
  return recovery
}

export async function resumeKrBillingOrder(orderId: string, bearerToken: string) {
  const response = await podoFetcher<unknown>(
    {
      url: `${ROOT}/orders/${encodeURIComponent(orderId)}/pay`,
      method: 'POST',
      header: { Authorization: `Bearer ${bearerToken}` },
      body: {},
    },
    { throwOnErrorResult: true },
  )
  const order = krPaymentOrderSchema.parse(response.data)
  if (order.orderId !== orderId) {
    throw new Error('KR_PAYMENT_ORDER_MISMATCH')
  }
  return order
}

export async function getKrCheckoutRecoveryUrl(bearerToken: string, currentOrderId?: string | null) {
  if (currentOrderId || !(await hasKrPurchaseCapability(bearerToken))) {
    return null
  }
  const active = await getActiveKrPaymentOrder(bearerToken)
  return active && !isKrOrderEditable(active) && !isKrOrderRetired(active) && active.status !== 'FAILED'
    ? krOrderResumePath(active)
    : null
}

export async function confirmKrPaymentOrder(
  orderId: string,
  bearerToken: string,
  approval?: { paymentToken: string; txId: string },
) {
  const response = await podoFetcher<unknown>(
    {
      url: `${ROOT}/orders/${encodeURIComponent(orderId)}/confirm`,
      method: 'POST',
      header: { Authorization: `Bearer ${bearerToken}` },
      body: approval ? { ...approval } : {},
    },
    { throwOnErrorResult: true },
  )
  const order = krPaymentOrderSchema.parse(response.data)
  if (order.orderId !== orderId) {
    throw new Error('KR_PAYMENT_ORDER_MISMATCH')
  }
  return order
}
